import { ReactNode, useEffect, useRef, useState } from "react";

export interface LayeredPanel {
  id: string;
  content?: ReactNode;
}

interface LayeredPanelsProps {
  panels: LayeredPanel[];
  stack: string[];
}

type Phase = "static" | "open" | "closing" | "hidden";

const SLIDE_MS = 300;
const SHEET_OPEN_MS = 500;

const PHASE_CLASSES: Record<Phase, string> = {
  static: "visible",
  open: "visible animate-in slide-in-from-right duration-300 ease-in-out motion-reduce:animate-none",
  closing:
    "visible animate-out slide-out-to-right fill-mode-forwards duration-300 ease-in-out motion-reduce:animate-none",
  hidden: "invisible",
};

interface LayerProps {
  isInStack: boolean;
  isTop: boolean;
  zIndex: number;
  skipInitialAnimation: boolean;
  children: ReactNode;
}

function Layer({
  isInStack,
  isTop,
  zIndex,
  skipInitialAnimation,
  children,
}: LayerProps) {
  const [phase, setPhase] = useState<Phase>(() => {
    if (!isInStack) return "hidden";
    return skipInitialAnimation ? "static" : "open";
  });

  useEffect(() => {
    if (isInStack) {
      setPhase((prev) => (prev === "static" || prev === "open" ? prev : "open"));
      return;
    }
    setPhase((prev) => (prev === "hidden" ? prev : "closing"));
    const timer = setTimeout(() => setPhase("hidden"), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [isInStack]);

  return (
    <div
      aria-hidden={!isTop}
      {...(isTop ? {} : ({ inert: "" } as object))}
      style={{ zIndex }}
      className={`absolute inset-0 overflow-y-auto bg-white ${PHASE_CLASSES[phase]}`}
    >
      {children}
    </div>
  );
}

export default function LayeredPanels({
  panels,
  stack: requestedStack,
}: LayeredPanelsProps) {
  // A newly added panel mounts off-screen first and only joins the visible stack
  // once its content has rendered, so heavy content can't swallow the slide-in
  // On first mount only the bottom panel is shown; the rest slide in after the sheet has opened
  const [stack, setStack] = useState(() => requestedStack.slice(0, 1));
  const isMountedRef = useRef(false);
  const requestedKey = requestedStack.join(",");

  useEffect(() => {
    const hasNewPanel = requestedStack.some((id) => !stack.includes(id));
    if (!hasNewPanel) {
      setStack(requestedStack);
      isMountedRef.current = true;
      return;
    }
    const delay = isMountedRef.current ? 32 : SHEET_OPEN_MS;
    const timer = setTimeout(() => {
      setStack(requestedStack);
      isMountedRef.current = true;
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedKey]);

  // Panels whose content disappears (e.g. details after deselecting) keep their last content and z-index so they can slide out
  const lastContentRef = useRef<Record<string, ReactNode>>({});
  const zIndexRef = useRef<Record<string, number>>({});
  const idsRef = useRef<string[]>([]);
  // Layers present on the first render ride along with the sidebar sheet's own slide
  const isFirstRenderRef = useRef(true);
  const skipInitialRef = useRef<Record<string, boolean>>({});

  panels.forEach(({ id, content }) => {
    if (content) {
      lastContentRef.current[id] = content;
    }
    if (!idsRef.current.includes(id)) {
      idsRef.current.push(id);
      skipInitialRef.current[id] =
        isFirstRenderRef.current && id === requestedStack[0];
    }
  });

  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  const topId = stack.length ? stack[stack.length - 1] : null;

  return (
    <div className="relative h-full w-full overflow-hidden">
      {idsRef.current.map((id) => {
        const content = lastContentRef.current[id];
        if (!content) return null;

        const index = stack.indexOf(id);
        const isInStack = index !== -1;
        if (isInStack) {
          zIndexRef.current[id] = index + 1;
        }

        return (
          <Layer
            key={id}
            isInStack={isInStack}
            isTop={id === topId}
            zIndex={zIndexRef.current[id] ?? 0}
            skipInitialAnimation={skipInitialRef.current[id]}
          >
            {content}
          </Layer>
        );
      })}
    </div>
  );
}
